import { FormikErrors, FormikHelpers, FormikTouched, setNestedObjectValues } from 'formik';

/**
 * Returns true if Formik error tree has any errors under the given top-level keys.
 */
export function branchHasErrors(
    errors: FormikErrors<unknown> | Record<string, unknown> | undefined | null,
    keys: string[]
): boolean {
    if (!errors) {
        return false;
    }

    return keys.some((key) => {
        const value = (errors as Record<string, unknown>)[key];
        if (value == null || value === '') {
            return false;
        }
        if (typeof value === 'string') {
            return value.length > 0;
        }
        if (Array.isArray(value)) {
            return value.some((item) => {
                if (item == null || item === false) {
                    return false;
                }
                if (typeof item === 'string') {
                    return item.length > 0;
                }
                if (typeof item === 'object') {
                    return Object.values(item as object).some(
                        (v) => v != null && v !== '' && v !== false
                    );
                }
                return Boolean(item);
            });
        }
        if (typeof value === 'object') {
            return Object.keys(value as object).length > 0;
        }
        return Boolean(value);
    });
}

type ChangeTabIfValidArgs<T> = {
    nextTab: number;
    activeTab: number;
    /** Field names that belong to each tab index (only those are validated when leaving that tab). */
    tabFieldKeys: string[][];
    values: T;
    validateForm: FormikHelpers<T>['validateForm'];
    setTouched: FormikHelpers<T>['setTouched'];
    setActiveTab: (tab: number) => void;
};

/**
 * Switch tabs only when the *current* tab's fields are valid.
 * On failure, marks those fields as touched so validation messages appear.
 */
export async function changeTabIfValid<T>({
    nextTab,
    activeTab,
    tabFieldKeys,
    values,
    validateForm,
    setTouched,
    setActiveTab,
}: ChangeTabIfValidArgs<T>): Promise<boolean> {
    if (nextTab === activeTab) {
        return true;
    }

    const keys = tabFieldKeys[activeTab] ?? [];
    const errors = await validateForm();

    if (branchHasErrors(errors as FormikErrors<Record<string, unknown>>, keys)) {
        const valuesRecord = values as Record<string, unknown>;
        const branchValues = keys.reduce<Record<string, unknown>>((acc, key) => {
            acc[key] = valuesRecord[key];
            return acc;
        }, {});
        void setTouched(setNestedObjectValues(branchValues, true) as FormikTouched<T>, true);
        return false;
    }

    setActiveTab(nextTab);
    return true;
}
